import type { FreightMode, DimensionUnit, WeightUnit } from '@freight-cbm/shared-types';

export interface EncodedState {
  packages: Array<{
    l: number;
    w: number;
    h: number;
    wt: number;
    q: number;
    du: DimensionUnit;
    wu: WeightUnit;
  }>;
  m: FreightMode;
  d?: number;
}

/**
 * Encode minimal app state to a URL-safe base64 string
 */
export function encodeStateToURL(data: {
  packages: Array<{ length: number; width: number; height: number; weight: number; quantity: number; dimUnit: DimensionUnit; weightUnit: WeightUnit }>;
  mode: FreightMode;
  dimDivisor?: number;
}): string {
  const encoded: EncodedState = {
    packages: data.packages.map((p) => ({
      l: p.length,
      w: p.width,
      h: p.height,
      wt: p.weight,
      q: p.quantity,
      du: p.dimUnit,
      wu: p.weightUnit,
    })),
    m: data.mode,
    ...(data.dimDivisor !== undefined ? { d: data.dimDivisor } : {}),
  };

  const json = JSON.stringify(encoded);
  return btoa(encodeURIComponent(json));
}

/**
 * Decode URL-safe base64 string back to app state
 */
export function decodeStateFromURL(encoded: string): EncodedState | null {
  try {
    const json = decodeURIComponent(atob(encoded));
    const parsed = JSON.parse(json) as EncodedState;

    // Validate structure
    if (!parsed.packages || !Array.isArray(parsed.packages) || !parsed.m) {
      return null;
    }

    for (const pkg of parsed.packages) {
      if (typeof pkg.l !== 'number' || typeof pkg.w !== 'number' || typeof pkg.h !== 'number' || typeof pkg.wt !== 'number') {
        return null;
      }
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Read state from current URL hash
 */
export function readStateFromHash(): EncodedState | null {
  const hash = window.location.hash.slice(1); // Remove #
  if (!hash) return null;
  return decodeStateFromURL(hash);
}

/**
 * Write state to current URL hash
 */
export function writeStateToHash(encoded: string): void {
  const url = new URL(window.location.href);
  url.hash = encoded;
  window.history.replaceState(null, '', url.toString());
}
